import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { toast } from 'react-toastify';
import messageService from '../../services/messageService';

const useStyles = makeStyles(() => ({
  root: { display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' },
  toolbar: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
    background: '#f9fafb', borderBottom: '1px solid #e5e7eb', flexShrink: 0,
  },
  iconBtn: {
    background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 5,
    color: '#6b7280', '&:hover': { background: '#f3f4f6' },
  },
  list: { flex: 1, overflowY: 'auto' },
  row: {
    display: 'flex', alignItems: 'flex-start', gap: 12, padding: '11px 16px',
    borderBottom: '1px solid #f0f1f3', cursor: 'pointer', transition: 'background 0.1s',
    '&:hover': { background: '#f9fafb' },
  },
  avatar: {
    width: 36, height: 36, borderRadius: '50%', background: '#78909c',
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 700, fontSize: 15, flexShrink: 0,
  },
  rowBody: { flex: 1, minWidth: 0 },
  rowTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 },
  recipient: { fontSize: 13.5, fontWeight: 500, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  date: { fontSize: 11.5, color: '#9ca3af', flexShrink: 0 },
  subject: { fontSize: 13, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 1 },
  preview: { fontSize: 12, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2, '@media (max-width:480px)': { display: 'none' } },
  tag: { padding: '2px 7px', borderRadius: 4, fontSize: 10.5, fontWeight: 600, flexShrink: 0 },
  tagInfo:     { background: '#dbeafe', color: '#1d4ed8' },
  tagFeedback: { background: '#dcfce7', color: '#15803d' },
  tagError:    { background: '#fee2e2', color: '#b91c1c' },
  empty: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, color: '#9ca3af', gap: 12, padding: 48, textAlign: 'center' },
  emptyIcon: { fontSize: 48, color: '#e5e7eb', marginBottom: 4 },
  emptyTitle: { fontSize: 16, fontWeight: 600, color: '#6b7280' },
  emptyDesc: { fontSize: 13, color: '#9ca3af', maxWidth: 240 },
  pagination: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '8px 16px', borderTop: '1px solid #e5e7eb', background: '#f9fafb',
    fontSize: 12, color: '#6b7280', flexShrink: 0,
  },
  pageBtn: {
    background: 'none', border: '1px solid #d1d5db', borderRadius: 5,
    padding: '3px 10px', cursor: 'pointer', fontSize: 12, color: '#374151',
    '&:disabled': { opacity: 0.4, cursor: 'default' },
    '&:not(:disabled):hover': { background: '#f3f4f6' },
  },
}));

const TYPE_TAGS = { INFO: { label: 'Info', cls: 'tagInfo' }, FEEDBACK: { label: 'Feedback', cls: 'tagFeedback' }, ERROR: { label: 'Error', cls: 'tagError' } };
const PAGE_SIZE = 20;

export default function MessagesSent({ userId }) {
  const classes = useStyles();
  const history = useHistory();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);

  const load = useCallback(() => {
    if (!userId) return;
    setLoading(true);
    messageService.getSentMessages(userId)
      .then(r => { setMessages(r.data?.items || []); setLoading(false); })
      .catch(() => { toast.error('Failed to load sent messages'); setLoading(false); });
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const fmtDate = (d) => {
    if (!d) return '';
    const dt = new Date(d), now = new Date();
    if (dt.toDateString() === now.toDateString()) return dt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const paged = messages.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const totalPages = Math.ceil(messages.length / PAGE_SIZE);

  return (
    <div className={classes.root}>
      <div className={classes.toolbar}>
        <button className={classes.iconBtn} onClick={load} disabled={loading} title="Refresh">
          <i className={`fa-solid fa-rotate-right ${loading ? 'fa-spin' : ''}`} style={{ fontSize: 14 }} />
        </button>
        <span style={{ fontSize: 12, color: '#6b7280' }}>{messages.length} sent</span>
      </div>

      <div className={classes.list}>
        {loading && !messages.length
          ? <div className={classes.empty}><CircularProgress size={28} /></div>
          : messages.length === 0
          ? <div className={classes.empty}>
              <i className={`fa-solid fa-paper-plane ${classes.emptyIcon}`} />
              <span className={classes.emptyTitle}>No sent messages</span>
              <span className={classes.emptyDesc}>Messages you send will appear here.</span>
            </div>
          : paged.map(msg => {
            const tag = TYPE_TAGS[msg.messageType?.typeCode];
            return (
              <div key={msg.id} className={classes.row}
                onClick={() => history.push(`/messages/conversation/${msg.recipientId}`)}>
                <div className={classes.avatar}>{(msg.recipientName?.charAt(0) || 'U').toUpperCase()}</div>
                <div className={classes.rowBody}>
                  <div className={classes.rowTop}>
                    <span className={classes.recipient}>{msg.recipientName || `User ${msg.recipientId}`}</span>
                    <span className={classes.date}>{fmtDate(msg.createdAt)}</span>
                  </div>
                  <div className={classes.subject}>{msg.subject}</div>
                  <div className={classes.preview}>{msg.content?.slice(0, 80)}{msg.content?.length > 80 ? '…' : ''}</div>
                </div>
                {tag && <span className={`${classes.tag} ${classes[tag.cls]}`}>{tag.label}</span>}
              </div>
            );
          })
        }
      </div>

      {messages.length > PAGE_SIZE && (
        <div className={classes.pagination}>
          <span>{page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, messages.length)} of {messages.length}</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className={classes.pageBtn} disabled={page === 0} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
            <button className={classes.pageBtn} disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next ›</button>
          </div>
        </div>
      )}
    </div>
  );
}
